import { useEffect, useState } from 'react';
import { EMPTY_SESSION_FILTERS } from '../api/types';
import {
  HARNESSES,
  OUTCOMES,
  PRIMARY_TASKS,
  SESSION_FEELS,
  type Outcome,
  type PrimaryTask,
  type SessionFeel,
  type SessionFilters,
} from '../api/types';

const HARNESS_LABELS: Record<string, string> = {
  'slicc': 'SLICC',
  'claude-code': 'Claude Code',
  'copilot': 'Copilot',
  'pi': 'Pi',
  'other': 'Other',
};
import { useToken } from '../auth/TokenContext';

interface Props {
  filters: SessionFilters;
  onChange: (filters: SessionFilters) => void;
}

const TASK_LABELS: Record<PrimaryTask, string> = {
  page_migration: 'Migration',
  refinement: 'Refinement',
  skill_authoring: 'Skill auth',
  content_generation: 'Content gen',
  other: 'Other',
};

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

interface ChipGroupProps<T extends string> {
  label: string;
  options: readonly T[];
  selected: T[];
  optionLabel?: (value: T) => string;
  onToggle: (value: T) => void;
}

function ChipGroup<T extends string>({
  label,
  options,
  selected,
  optionLabel,
  onToggle,
}: ChipGroupProps<T>): JSX.Element {
  return (
    <div className="filter-chip-group">
      <span className="filter-chip-group-label">{label}</span>
      {options.map((value) => {
        const isActive = selected.includes(value);
        return (
          <button
            key={value}
            type="button"
            className={`filter-chip${isActive ? ' is-active' : ''}`}
            onClick={() => onToggle(value)}
            aria-pressed={isActive}
          >
            {optionLabel ? optionLabel(value) : value}
          </button>
        );
      })}
    </div>
  );
}

interface RangeRowProps {
  label: string;
  inputType: 'date' | 'number';
  unit?: string;
  fromValue: string;
  toValue: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  fromPlaceholder?: string;
  toPlaceholder?: string;
}

function RangeRow({
  label,
  inputType,
  unit,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  fromPlaceholder,
  toPlaceholder,
}: RangeRowProps): JSX.Element {
  return (
    <div className="filter-range-row">
      <span className="filter-chip-group-label">{label}</span>
      <input
        type={inputType}
        className="filter-range-input"
        value={fromValue}
        placeholder={fromPlaceholder ?? 'min'}
        min={inputType === 'number' ? 0 : undefined}
        onChange={(e) => onFromChange(e.target.value)}
      />
      <span className="filter-range-sep">to</span>
      <input
        type={inputType}
        className="filter-range-input"
        value={toValue}
        placeholder={toPlaceholder ?? 'max'}
        min={inputType === 'number' ? 0 : undefined}
        onChange={(e) => onToChange(e.target.value)}
      />
      {unit && <span className="filter-range-unit">{unit}</span>}
    </div>
  );
}

function dateOnlyOrEmpty(iso: string | null): string {
  if (!iso) return '';
  // ISO datetimes look like 2026-05-08T10:00:00.000Z; the date input
  // wants 2026-05-08. Strip the time portion.
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
}

function dateInputToIso(value: string, end: boolean): string | null {
  // <input type="date"> emits YYYY-MM-DD. Anchor to UTC midnight at the
  // start of the day, or to the last instant of the day for the upper
  // bound — matches the inclusive "started after / before" semantic.
  if (!value) return null;
  return end ? `${value}T23:59:59.999Z` : `${value}T00:00:00.000Z`;
}

function numberInputToInt(value: string): number | null {
  if (value === '') return null;
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Text input that commits on blur / Enter rather than per-keystroke,
 * so typing "alice" doesn't fire five DB queries. Resets the local
 * draft when the parent clears filters externally.
 */
function UserIdInput({
  value,
  onCommit,
}: {
  value: string | null;
  onCommit: (next: string | null) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(value ?? '');

  // Sync the local draft when the parent resets filters (e.g. Clear
  // filters button). We intentionally let user keystrokes override
  // the parent's `null` between renders.
  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const commit = (): void => {
    const trimmed = draft.trim();
    const next = trimmed === '' ? null : trimmed;
    if ((value ?? null) === next) return;
    onCommit(next);
  };

  return (
    <div className="filter-range-row">
      <span className="filter-chip-group-label">User contains:</span>
      <input
        type="text"
        className="filter-range-input filter-userid-input"
        value={draft}
        placeholder="e.g. alice"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
      />
      {draft !== '' && (
        <button
          type="button"
          className="filter-userid-clear"
          onClick={() => {
            setDraft('');
            onCommit(null);
          }}
          aria-label="Clear user filter"
          title="Clear"
        >
          ✕
        </button>
      )}
    </div>
  );
}

export function SessionsFilterBar({ filters, onChange }: Props): JSX.Element {
  const { session } = useToken();
  const canViewFullEmail = session?.canViewFullEmail ?? false;
  return (
    <div className="sessions-filter-bar" role="region" aria-label="Session filters">
      <ChipGroup
        label="Task:"
        options={PRIMARY_TASKS}
        selected={filters.tasks}
        optionLabel={(v) => TASK_LABELS[v]}
        onToggle={(v) => onChange({ ...filters, tasks: toggle(filters.tasks, v) as PrimaryTask[] })}
      />
      <ChipGroup
        label="Outcome:"
        options={OUTCOMES}
        selected={filters.outcomes}
        onToggle={(v) =>
          onChange({ ...filters, outcomes: toggle(filters.outcomes, v) as Outcome[] })
        }
      />
      <ChipGroup
        label="Feel:"
        options={SESSION_FEELS}
        selected={filters.feels}
        onToggle={(v) =>
          onChange({ ...filters, feels: toggle(filters.feels, v) as SessionFeel[] })
        }
      />
      <ChipGroup
        label="Harness:"
        options={HARNESSES}
        selected={filters.harnesses}
        optionLabel={(v) => HARNESS_LABELS[v] ?? v}
        onToggle={(v) => onChange({ ...filters, harnesses: toggle(filters.harnesses, v) })}
      />
      <RangeRow
        label="Started:"
        inputType="date"
        fromValue={dateOnlyOrEmpty(filters.startedAfter)}
        toValue={dateOnlyOrEmpty(filters.startedBefore)}
        onFromChange={(v) => onChange({ ...filters, startedAfter: dateInputToIso(v, false) })}
        onToChange={(v) => onChange({ ...filters, startedBefore: dateInputToIso(v, true) })}
      />
      <RangeRow
        label="Duration:"
        inputType="number"
        unit="min"
        fromValue={filters.durationMinMin === null ? '' : String(filters.durationMinMin)}
        toValue={filters.durationMinMax === null ? '' : String(filters.durationMinMax)}
        onFromChange={(v) => onChange({ ...filters, durationMinMin: numberInputToInt(v) })}
        onToChange={(v) => onChange({ ...filters, durationMinMax: numberInputToInt(v) })}
      />
      <RangeRow
        label="Events:"
        inputType="number"
        fromValue={filters.eventCountMin === null ? '' : String(filters.eventCountMin)}
        toValue={filters.eventCountMax === null ? '' : String(filters.eventCountMax)}
        onFromChange={(v) => onChange({ ...filters, eventCountMin: numberInputToInt(v) })}
        onToChange={(v) => onChange({ ...filters, eventCountMax: numberInputToInt(v) })}
      />
      {canViewFullEmail && (
        <UserIdInput
          value={filters.userIdQuery}
          onCommit={(next) => onChange({ ...filters, userIdQuery: next })}
        />
      )}
      <button
        type="button"
        className="filter-clear"
        onClick={() => onChange(EMPTY_SESSION_FILTERS)}
      >
        Clear filters
      </button>
    </div>
  );
}
